// Shared line-item money helpers — single source of truth for how a line's
// total and cost are computed, and for how a document's totals are assembled.
// A line flagged `isReturnCredit` is a credit back to the customer: qty and
// unitPrice stay POSITIVE in storage/inputs, and the sign is applied here so
// the line subtracts from subtotal, tax, cost, and PDFs.

// ── Money rounding ──
// Every figure a customer can SEE is rounded to cents at the point it is
// produced, not at the point it is printed. That is what makes the printed
// lines add up: the subtotal is the sum of the same 2-decimal line totals the
// customer reads off the page, and the grand total is built from the same
// rounded subtotal/tax/delivery shown above it.
//
// Half-away-from-zero, so a return credit rounds by the same magnitude as the
// equivalent charge (plain Math.round is asymmetric: -0.005 → -0, 0.005 → 1).
// The relative epsilon nudge covers values that are an exact half-cent in
// decimal but land a hair BELOW it in binary (6 × 34.7875 = 208.725 is stored
// as 208.72499999999997 on some inputs and would otherwise round DOWN).
export const round2 = (n) => {
  const v = Number(n) || 0;
  const sign = v < 0 ? -1 : 1;
  const abs = Math.abs(v);
  return sign * Math.round((abs + abs * Number.EPSILON + Number.EPSILON) * 100) / 100;
};

// Sign multiplier for a line: -1 for a return credit, +1 otherwise.
export const lineSign = (item) => (item && item.isReturnCredit ? -1 : 1);

// A line's contribution to revenue totals (subtotal, row totals, PDF row total).
// Rounded to cents HERE so the value summed into the subtotal is byte-identical
// to the value printed on the row.
export const lineTotal = (item) =>
  round2(lineSign(item) * (parseFloat(item.qty) || 0) * (parseFloat(item.unitPrice) || 0));

// A line's contribution to cost (Office Use). `resolvedUnitCost` is the already-
// resolved per-unit cost from the existing cost-source priority (template
// costPrice / non-stock cost / productCostMap) — this helper only applies the
// sign and the qty multiply so that priority logic stays untouched.
export const lineCost = (item, resolvedUnitCost) =>
  lineSign(item) * (parseFloat(item.qty) || 0) * (parseFloat(resolvedUnitCost) || 0);

// ── WHAT A LINE COSTS PER UNIT ────────────────────────────────────────────
//
// THE one definition of the cost-source priority. It was the same seventeen
// lines inlined in NewDocument and EditDocument's totalCost reducers; the line
// item editor needs the same answer, and three copies of a money rule is two
// too many.
//
// The priority, unchanged:
//   1. a TEMPLATE line's own stored costPrice (the template's defaultCost);
//   2. a NON-STOCK line's own typed cost;
//   3. the product's price-list costPrice, by productId;
//   4. the same, by SKU — a destructive re-import can orphan a productId while
//      the SKU survives, and this is what recovers the cost;
//   5. 0 — nothing to go on.
//
// ⚠️ THE LOOKUP IS BUILT THE WAY THE PAGES BUILT IT, deliberately: every
// product is indexed whether active or not, and on duplicate SKUs the LAST
// product wins, because that is what repeated map assignment did. Switching to
// a first-match find() here would quietly change the cost of a line whose SKU
// appears twice in the price list.
export function buildCostLookup(products) {
  const byId = {};
  const bySku = {};
  (Array.isArray(products) ? products : []).forEach(p => {
    if (!p) return;
    byId[p.id] = parseFloat(p.costPrice || 0);
    if (p.sku) bySku[String(p.sku).trim()] = parseFloat(p.costPrice || 0);
  });
  return { byId, bySku, __costLookup: true };
}

/**
 * The per-unit cost of one line. Pass either the products array or a lookup
 * from buildCostLookup — the lookup when it is being called per line, so the
 * index is built once rather than once per line.
 */
export function resolveUnitCost(item, productsOrLookup) {
  if (!item || item.isTextNote) return 0;      // a note has no cost
  const lookup = productsOrLookup && productsOrLookup.__costLookup
    ? productsOrLookup
    : buildCostLookup(productsOrLookup);

  if (item.fromTemplate && item.costPrice !== undefined) return parseFloat(item.costPrice) || 0;
  if (item.isNonStockCustom) return parseFloat(item.cost) || 0;
  if (item.productId || item.sku) {
    let c = (item.productId != null) ? lookup.byId[item.productId] : undefined;
    if (c === undefined && item.sku) c = lookup.bySku[String(item.sku).trim()];
    return parseFloat(c) || 0;
  }
  return 0;
}

// ── IS THE COST ACTUALLY KNOWN? ───────────────────────────────────────────
//
// Only a finite number ABOVE ZERO counts. Everything else — a deleted product,
// an estimator line whose SKU is not in the price list, a bundle item that was
// not found, a non-stock line where the cost box was left blank, a product with
// a blank costPrice — resolves to 0, and 0 would show as a 100% margin.
//
// ⚠️ THE KNOWN COST OF THIS: a genuinely FREE item, cost £0 on purpose, also
// reads as unavailable. That is deliberate. A stored 0 is indistinguishable
// from "nobody filled it in", and a false 100% margin on a quote somebody
// prices from is worse than saying nothing at all.
export const isUnitCostKnown = (unitCost) => Number.isFinite(unitCost) && unitCost > 0;

/**
 * Margin on one line, in dollars: what was charged less what it cost.
 *
 * Both halves carry the line's sign, so a RETURN CREDIT comes out NEGATIVE —
 * margin handed back to the customer. Rounded through the same round2 as every
 * other figure the app shows.
 */
export const lineMargin = (item, unitCost) => round2(lineTotal(item) - lineCost(item, unitCost));

/**
 * Margin as a percentage of what was charged, or null when there is nothing to
 * divide by. The caller decides whether a percentage is meaningful: on a return
 * credit it is not shown, because a positive percent beside a negative dollar
 * figure invites misreading.
 */
export function lineMarginPct(item, unitCost) {
  const total = lineTotal(item);
  if (!total) return null;
  return (lineMargin(item, unitCost) / total) * 100;
}

// ── Document totals ──
// Single source of truth for the totals block, used by NewDocument and
// EditDocument for both the on-screen figures and the values written to
// Firestore. Business rules are unchanged from the previous inline copies:
// the discount is a percentage off the subtotal, tax is charged on the
// POST-discount base, delivery is never discounted and is never taxed, and a
// tax-exempt document is charged no tax. Only the ROUNDING changed.
export function computeDocTotals(lineItems, {
  discountPercent = 0,
  deliveryFee = 0,
  taxExempt = false,
  taxRate = 0.06,
} = {}) {
  const items = Array.isArray(lineItems) ? lineItems : [];

  // Sum of the ROUNDED per-line totals — the numbers printed on the rows.
  // The outer round2 only clears float-accumulation residue from the reduce.
  const subtotal = round2(items.reduce((sum, item) => {
    if (item && item.isTextNote) return sum; // text notes contribute nothing
    return sum + lineTotal(item);
  }, 0));

  const discountAmount = round2(subtotal * ((parseFloat(discountPercent) || 0) / 100));
  const taxableBase = round2(subtotal - discountAmount);
  const delivery = round2(parseFloat(deliveryFee) || 0);
  const tax = taxExempt ? 0 : round2(taxableBase * taxRate);
  // Built from the SAME rounded operands displayed above it, so
  // subtotal − discount + tax + delivery always ties out to the cent.
  const grandTotal = round2(taxableBase + delivery + tax);

  return { subtotal, discountAmount, taxableBase, delivery, tax, grandTotal };
}
